import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import Container from 'react-bootstrap/Container';
import ListGroup from 'react-bootstrap/ListGroup';
import Badge from 'react-bootstrap/Badge';
import styled from 'styled-components';
import ErrorMessage from '../components/ErrorMessage';
import elearningApi from 'api/elearningApi';
import { PATH } from 'constants/path';
import { MdQuiz, MdInbox, MdArrowBack } from 'react-icons/md';
import TimeExceedWarning from '../components/TimeExceedWarning';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectElearningData,
  selectQuizzes,
  updateElearningData,
} from 'store/elearning.slice';
import { useNavigate } from 'react-router-dom';
import {
  PrimaryButton,
  Spinner,
  colors,
  radius,
  shadow,
  spacing,
  typography,
} from 'design-system';

const MOBILE = '@media (max-width: 768px)';

function ElearningStudentTestPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [quizzes, setQuizzes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingAttempts, setIsCheckingAttempts] = useState(false);
  const [error, setError] = useState(null);
  const elearningData = useSelector(selectElearningData);
  const { elearningCourses, isLimitExceeded, timeLimitPerDay, totalTodayTime, elearningUser } =
    elearningData;
  const quizzesMap = useSelector(selectQuizzes);
  const courses = useMemo(() => {
    return Object.values(elearningCourses || {}).filter((course) => course?.visible);
  }, [elearningCourses]);

  // attemptsByQuizId: { [quizId]: { lastAttempt, hasAbandoned, hasInprogress } }
  // Dùng để hiển thị badge trạng thái + đảm bảo nút "Thực hiện lại" vẫn hiện
  // khi admin huỷ attempt cuối (state='abandoned'). Backend trả abandoned từ
  // mongo cache (mod_quiz_get_user_attempts thường không trả về abandoned
  // nếu plugin Moodle chưa cài) — phải gọi backend `getBatchQuizAttempts`
  // thay vì Moodle WS trực tiếp.
  const [attemptsByQuizId, setAttemptsByQuizId] = useState({});

  const loadInitialData = useCallback(async (courseIds, currentMap) => {
    if (!courseIds.length) {
      setError('Bạn chưa tham gia khoá học nào.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setQuizzes([]);

    try {
      // Use per-course cached quizzes when present; fetch only the
      // missing course IDs. Avoids a round-trip when navigating back
      // from a quiz detail page or re-mounting after navigation.
      const missing = courseIds.filter((id) => !currentMap?.[id]);
      let fetched = [];
      if (missing.length > 0) {
        const res = await elearningApi.quizzesByCourses(missing);
        fetched = Array.isArray(res) ? res : [];
        if (!Array.isArray(res)) {
          throw new Error('Không thể lấy danh sách quiz. Phản hồi không hợp lệ.');
        }
        // Bucket fetched quizzes by course and merge into the slice.
        const next = { ...(currentMap || {}) };
        for (const id of missing) {
          next[id] = fetched.filter((q) => q.course === id || String(q.course) === String(id));
        }
        dispatch(updateElearningData({ quizzes: next }));
      }
      const allQuizzes = courseIds.flatMap((id) => {
        if (currentMap?.[id]) return currentMap[id];
        return fetched.filter((q) => q.course === id || String(q.course) === String(id));
      });
      setQuizzes(allQuizzes.filter((quiz) => quiz.timelimit > 0));
    } catch (err) {
      setError(err.message || 'Đã xảy ra lỗi khi tải dữ liệu ban đầu.');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadInitialData(courses?.map((course) => course.id) || [], quizzesMap);
    // quizzesMap intentionally omitted — only re-seed when course list changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courses]);

  // Sau khi có quizzes → fetch batch attempts của HV cho TẤT CẢ quiz để
  // biết quiz nào có attempt abandoned/inprogress.
  useEffect(() => {
    const userId = elearningUser?.elearningUserId;
    const quizIds = quizzes.map((q) => q.id).filter(Boolean);
    if (!userId || quizIds.length === 0) {
      setAttemptsByQuizId({});
      return;
    }
    let cancelled = false;
    setIsCheckingAttempts(true);
    elearningApi.getBatchQuizAttempts(userId, quizIds)
      .then((res) => {
        if (cancelled) return;
        const byId = {};
        const data = res?.data || {};
        for (const qid of Object.keys(data)) {
          const attempts = data[qid]?.attempts || [];
          if (attempts.length === 0) continue;
          // Sort theo timeStart descending để lấy lastAttempt
          const sorted = attempts.slice().sort((a, b) => (b.timeStart || 0) - (a.timeStart || 0));
          const lastAttempt = sorted[0];
          byId[qid] = {
            lastAttempt,
            hasAbandoned: attempts.some((a) => a.state === 'abandoned'),
            hasInprogress: attempts.some((a) => a.state === 'inprogress'),
          };
        }
        setAttemptsByQuizId(byId);
      })
      .catch((err) => {
        console.error('Failed to fetch batch quiz attempts:', err);
      })
      .finally(() => {
        if (!cancelled) setIsCheckingAttempts(false);
      });
    return () => { cancelled = true; };
  }, [quizzes, elearningUser?.elearningUserId]);

  if (isLimitExceeded) {
    return (
      <TimeExceedWarning
        timeLimitPerDay={timeLimitPerDay}
        totalTodayTime={totalTodayTime}
      />
    );
  }

  const renderContent = () => {
    if (isLoading) {
      return <Spinner block label='Đang tải danh sách bài kiểm tra...' />;
    }

    if (error && !isLoading) {
      return (
        <ErrorWrap>
          <ErrorMessage message='Đã có lỗi khi tải danh sách bài kiểm tra.' />
          <PrimaryButton
            variant='secondary'
            size='sm'
            onClick={() => loadInitialData(courses?.map((course) => course.id) || [], quizzesMap)}
          >
            Thử lại
          </PrimaryButton>
        </ErrorWrap>
      );
    }

    if (quizzes.length > 0) {
      return (
        <QuizList>
          {quizzes.map((quiz) => {
            const meta = attemptsByQuizId[quiz.id];
            const lastState = meta?.lastAttempt?.state;
            // Nút label thay đổi theo trạng thái attempt cuối:
            //  - abandoned → "Thực hiện lại" (làm lại từ đầu)
            //  - inprogress → "Tiếp tục bài làm"
            //  - finished → "Thực hiện lại"
            //  - chưa có → "Thực hiện"
            let buttonLabel = 'Thực hiện';
            if (lastState === 'inprogress') buttonLabel = 'Tiếp tục bài làm';
            else if (lastState === 'finished' || lastState === 'abandoned') buttonLabel = 'Thực hiện lại';
            return (
              <QuizItem key={quiz.id}>
                <QuizInfo>
                  <QuizTitle>
                    {quiz.name}
                    {lastState === 'abandoned' && (
                      <Badge bg='danger' title='Bài làm trước đã bị admin huỷ'>
                        Bài làm trước đã bị huỷ
                      </Badge>
                    )}
                    {lastState === 'inprogress' && (
                      <Badge bg='warning' text='dark'>Đang làm dở</Badge>
                    )}
                  </QuizTitle>
                  <QuizMeta>
                    Số câu hỏi: {quiz?.questioncount || ''}
                  </QuizMeta>
                  <QuizMeta>
                    Điểm đạt: {quiz?.gradepass}/{quiz.grade}
                  </QuizMeta>
                  {quiz.timeopen > 0 && (
                    <QuizMeta>
                      Mở lúc: {new Date(quiz?.timeopen * 1000).toLocaleString()}
                    </QuizMeta>
                  )}
                  {quiz.timeclose > 0 && (
                    <QuizMeta>
                      Đóng lúc:{' '}
                      {new Date(quiz?.timeclose * 1000).toLocaleString()}
                    </QuizMeta>
                  )}
                  {quiz?.timelimit > 0 ? (
                    <QuizMeta>
                      Thời gian làm bài:{' '}
                      <strong>{Math.floor(quiz.timelimit / 60)} phút</strong>
                    </QuizMeta>
                  ) : (
                    <QuizMeta>Không giới hạn thời gian</QuizMeta>
                  )}
                </QuizInfo>
                <PrimaryButton
                  variant='ghost'
                  size='sm'
                  onClick={() => {
                    navigate(
                      `${PATH.ELEARNING.STUDENT.TEST_DETAIL.replace(
                        ':id',
                        quiz.id
                      )}?c=${quiz.course}&m=${quiz.coursemodule}`
                    );
                  }}
                >
                  <MdQuiz className='me-1' />
                  {buttonLabel}
                </PrimaryButton>
              </QuizItem>
            );
          })}
        </QuizList>
      );
    }

    if (!isLoading && !error && quizzes.length === 0) {
      return (
        <EmptyState role='status' aria-live='polite'>
          <EmptyIconWrap>
            <MdInbox size={56} />
          </EmptyIconWrap>
          <EmptyTitle>Chưa có bài kiểm tra</EmptyTitle>
          {isCheckingAttempts && (
            <EmptyHint>
              <Spinner size='sm' /> Đang kiểm tra trạng thái bài làm...
            </EmptyHint>
          )}
          <EmptyActions>
            <PrimaryButton
              variant='secondary'
              size='md'
              onClick={() => navigate(PATH.ELEARNING.STUDENT.COURSE)}
            >
              <MdArrowBack className='me-1' /> Về danh sách môn học
            </PrimaryButton>
          </EmptyActions>
        </EmptyState>
      );
    }

    return (
      <WaitingState>
        <WaitingText>Đang chờ dữ liệu...</WaitingText>
        <PrimaryButton
          variant='secondary'
          size='sm'
          onClick={() => loadInitialData(courses?.map((course) => course.id))}
        >
          Tải lại
        </PrimaryButton>
      </WaitingState>
    );
  };

  return (
    <PageWrap>
      <Container className='py-3 py-md-4'>
        <PageTitle>Chọn bài kiểm tra</PageTitle>
        {renderContent()}
      </Container>
    </PageWrap>
  );
}

/* ─── styled ─── */

const PageWrap = styled.div`
  min-height: 100vh;
  background: ${colors.background};
  overflow-y: auto;
`;

const PageTitle = styled.h2`
  font-size: ${typography.size.h2};
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};
  margin-bottom: ${spacing[5]};

  ${MOBILE} {
    font-size: ${typography.size.h3};
    margin-bottom: ${spacing[4]};
  }
`;

const QuizList = styled(ListGroup)`
  border-radius: ${radius.lg};
  overflow: hidden;
  box-shadow: ${shadow.sm};
  border: 1px solid ${colors.border};
`;

const QuizItem = styled(ListGroup.Item)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing[3]};
  padding: ${spacing[4]};
  background: ${colors.surface};
  border-color: ${colors.border};

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;
  }
`;

const QuizInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const QuizTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${colors.gray900};
  margin-bottom: ${spacing[2]};
`;

const QuizMeta = styled.div`
  display: block;
  font-size: ${typography.size.sm};
  color: ${colors.gray500};
  margin-top: ${spacing[1]};
`;

const ErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing[3]};
  margin-top: ${spacing[4]};
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${spacing[3]};
  padding: ${spacing[10]} ${spacing[5]};
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  margin-top: ${spacing[4]};

  ${MOBILE} {
    padding: ${spacing[8]} ${spacing[4]};
  }
`;

const EmptyIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: ${colors.gray100 || '#F3F4F6'};
  color: ${colors.gray400 || '#9CA3AF'};
  margin-bottom: ${spacing[1]};

  ${MOBILE} {
    width: 80px;
    height: 80px;
  }
`;

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: ${typography.size.h3};
  font-weight: ${typography.weight.bold};
  color: ${colors.gray900};

  ${MOBILE} {
    font-size: ${typography.size.lg};
  }
`;

const EmptySubtitle = styled.p`
  margin: 0;
  max-width: 480px;
  font-size: ${typography.size.base};
  color: ${colors.gray500};
  line-height: 1.5;
`;

const EmptyHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.size.sm};
  color: ${colors.gray400 || '#9CA3AF'};
  margin-top: ${spacing[1]};
`;

const EmptyActions = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
`;

const WaitingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing[3]};
  margin-top: ${spacing[10]};
`;

const WaitingText = styled.p`
  margin: 0;
  font-size: ${typography.size.base};
  color: ${colors.gray500};
`;

export default ElearningStudentTestPage;
